import React, { useState } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Table,
  Modal,
  Form,
  Input,
  Switch,
  Popconfirm,
  message,
  Empty,
  ColorPicker,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { Color } from 'antd/es/color-picker';
import {
  useClientTierConfigs,
  useCreateClientTier,
  useUpdateClientTier,
  useDeleteClientTier,
} from '../../ai-crm/hooks/useClientTiers';
import type { ClientTierConfig } from '../../ai-crm/api/clientTierApi';

const { Title, Text } = Typography;

interface FormValues {
  name: string;
  color: string | Color;
  isDefault: boolean;
}

const toHex = (c: string | Color | undefined): string => {
  if (!c) return '#6B7280';
  if (typeof c === 'string') return c;
  return c.toHexString();
};

const ClientTiersSettings: React.FC = () => {
  const { data: tiers, isLoading } = useClientTierConfigs();
  const createMut = useCreateClientTier();
  const updateMut = useUpdateClientTier();
  const deleteMut = useDeleteClientTier();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ClientTierConfig | null>(null);
  const [form] = Form.useForm<FormValues>();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ name: '', color: '#6B7280', isDefault: false });
    setModalOpen(true);
  };

  const openEdit = (tier: ClientTierConfig) => {
    setEditing(tier);
    form.setFieldsValue({ name: tier.name, color: tier.color, isDefault: tier.isDefault });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name.trim(),
        color: toHex(values.color),
        isDefault: values.isDefault,
      };
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, input: payload });
        message.success('Tier updated');
      } else {
        await createMut.mutateAsync(payload);
        message.success('Tier created');
      }
      setModalOpen(false);
    } catch (err: any) {
      if (err?.response?.data?.message) {
        message.error(err.response.data.message);
      } else if (err?.errorFields) {
        // antd validation error — already shown
      } else {
        message.error(err?.message || 'Failed to save tier');
      }
    }
  };

  const handleDelete = async (tier: ClientTierConfig) => {
    try {
      await deleteMut.mutateAsync(tier.id);
      message.success(`Removed "${tier.name}"`);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to delete tier');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, row: ClientTierConfig) => (
        <Tag
          style={{
            backgroundColor: row.color,
            color: '#fff',
            border: 'none',
            fontWeight: 600,
            padding: '2px 10px',
          }}
        >
          {name}
        </Tag>
      ),
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      width: 120,
      render: (color: string) => (
        <Space>
          <div style={{ width: 18, height: 18, borderRadius: 4, backgroundColor: color, border: '1px solid #ddd' }} />
          <Text type="secondary" style={{ fontFamily: 'monospace' }}>{color}</Text>
        </Space>
      ),
    },
    {
      title: 'Default',
      dataIndex: 'isDefault',
      key: 'isDefault',
      width: 100,
      render: (isDefault: boolean) => (isDefault ? <Tag color="blue">Default</Tag> : null),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_: unknown, row: ClientTierConfig) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Popconfirm
            title={`Delete "${row.name}"?`}
            description="Clients currently using this tier must be reassigned first."
            onConfirm={() => handleDelete(row)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>Client Tiers</Title>
            <Text type="secondary">
              Define the tier labels (e.g. Standard, Premium, VIP) shown on client records.
              Each tier can have its own color. One tier can be marked as the default for new clients.
            </Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Tier
          </Button>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={tiers ?? []}
          loading={isLoading}
          pagination={false}
          locale={{
            emptyText: <Empty description="No tiers configured yet. Click 'Add Tier' to create your first one." />,
          }}
        />
      </Space>

      <Modal
        title={editing ? `Edit Tier — ${editing.name}` : 'Add Tier'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText="Save"
        confirmLoading={createMut.isPending || updateMut.isPending}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="Tier Name"
            rules={[
              { required: true, message: 'Tier name is required' },
              { max: 50, message: 'Keep it under 50 characters' },
            ]}
          >
            <Input placeholder="e.g. Premium" />
          </Form.Item>
          <Form.Item name="color" label="Color" rules={[{ required: true }]}>
            <ColorPicker showText format="hex" />
          </Form.Item>
          <Form.Item name="isDefault" label="Default for new clients" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ClientTiersSettings;
